import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Switch, TextArea } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Settings, CheckCircle, AlertTriangle, RotateCcw, MessageSquare, Megaphone, Clock } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import appConfig from '../../config/appConfig';

import { usePermission } from '../../hooks/usePermission';

/**
 * Super-admin System Settings. Runtime toggles (no rebuild):
 *   • AI Assistant (chat) on/off
 *   • Maintenance banner (shown to all users)
 * With a settings service configured (VITE_SETTINGS_URL / VITE_AGENT_URL) these
 * persist SERVER-SIDE, so a save reaches every user on their next load — no
 * config.json upload. Without one, changes apply to this device only (demo).
 * Page design mirrors the member portal.
 */

const isSuperAdmin = (user) => String(user?.userRole || user?.role || '').toUpperCase() === 'SUPER ADMIN' || String(user?.userRole || user?.role || '').toUpperCase() === 'ADMIN';

const cardStyle = { background: '#fff', border: '1px solid #E8ECF0', borderRadius: 14, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
const iconChip = (bg, color) => ({ width: 36, height: 36, borderRadius: 10, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });
const headRow = { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18, flexWrap: 'wrap' };
const h2Style = { fontSize: 16, fontWeight: 700, color: '#0F172A', margin: 0, marginBottom: 4 };
const pStyle = { fontSize: 13, color: '#64748B', margin: 0, lineHeight: 1.5 };
const okLine = { display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 12, color: '#166534', fontWeight: 600 };
const errLine = { display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 12, color: '#DC2626', fontWeight: 600 };

export default function SystemSettingsPage() {
    const { user } = useAuth();
    const { can, canAny, canAll } = usePermission();

  const global = appConfig.hasSettingsBackend(); // server-persisted (all users) vs device-only
  const scope = global ? 'all users' : 'this device';

  const [assistantEnabled, setAssistantEnabled] = useState(appConfig.getAssistantEnabled());
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [assistantMsg, setAssistantMsg] = useState(null); // { ok, text }

  const [loginAssistant, setLoginAssistant] = useState(appConfig.getLoginAssistantEnabled());
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginMsg, setLoginMsg] = useState(null);

  const [maintEnabled, setMaintEnabled] = useState(appConfig.getMaintenanceEnabled());
  const [maintMessage, setMaintMessage] = useState(appConfig.getMaintenanceMessage());
  const [maintBusy, setMaintBusy] = useState(false);
  const [maintMsg, setMaintMsg] = useState(null);

  const [inact, setInact] = useState(appConfig.getInactivity()); // { enabled, days }
  const [inactBusy, setInactBusy] = useState(false);
    const [inactMsg, setInactMsg] = useState(null);
    const [accessPermissions, setAccessPermissions] = useState({});


    /***********************Access Permissions ******************************/

    /***********************Access Permissions ******************************/


  useEffect(() => appConfig.subscribe(() => {
    setAssistantEnabled(appConfig.getAssistantEnabled());
    setLoginAssistant(appConfig.getLoginAssistantEnabled());
  }), []);

    if (!isSuperAdmin(user)) return <Navigate to="/" replace />;


    useEffect(() => {
        setAccessPermissions({
            canUpdateSystemSettings: can('superAdmin', 'systemSettings', 'updateSystemSettings')
        });
    },[user])

  const toggleLoginAssistant = async (value) => {
    setLoginAssistant(value); // optimistic
    setLoginBusy(true); setLoginMsg(null);
    try {
      await appConfig.saveLoginAssistantEnabled(value);
      setLoginMsg({ ok: true, text: `${value ? 'Enabled' : 'Disabled'} for ${scope}.` });
    } catch (err) {
      setLoginAssistant(!value); // revert
      setLoginMsg({ ok: false, text: `Couldn’t save: ${err.message}` });
    } finally { setLoginBusy(false); }
  };

  const toggleAssistant = async (value) => {
    setAssistantEnabled(value); // optimistic
    setAssistantBusy(true); setAssistantMsg(null);
    try {
      await appConfig.saveAssistantEnabled(value);
      setAssistantMsg({ ok: true, text: `${value ? 'Enabled' : 'Disabled'} for ${scope}.` });
    } catch (err) {
      setAssistantEnabled(!value); // revert
      setAssistantMsg({ ok: false, text: `Couldn’t save: ${err.message}` });
    } finally { setAssistantBusy(false); }
  };

  const saveMaintenance = async (enabled, message) => {
    setMaintBusy(true); setMaintMsg(null);
    try {
      await appConfig.saveMaintenance(enabled, message);
      setMaintMessage(appConfig.getMaintenanceMessage());
      setMaintMsg({ ok: true, text: `Saved for ${scope}.` });
    } catch (err) {
      setMaintMsg({ ok: false, text: `Couldn’t save: ${err.message}` });
    } finally { setMaintBusy(false); }
  };
  const clearMaintenance = () => { setMaintEnabled(false); setMaintMessage(''); saveMaintenance(false, ''); };

  const saveInactivity = async (enabled, days) => {
    setInactBusy(true); setInactMsg(null);
    try {
      await appConfig.saveInactivity(enabled, days);
      setInact(appConfig.getInactivity());
      setInactMsg({ ok: true, text: `Saved for ${scope}.` });
    } catch (err) {
      setInactMsg({ ok: false, text: `Couldn’t save: ${err.message}` });
    } finally { setInactBusy(false); }
  };

  return (
    <div>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #134E4A 0%, #0F766E 50%, #0D9488 100%)', borderRadius: 16, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Settings size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0, marginBottom: 4 }}>System Settings</h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0 }}>
              {global ? 'Runtime configuration — changes apply to all users on their next load.' : 'Runtime configuration for this Super Admin session and device.'}
            </p>
          </div>
        </div>
      </div>

      {!global && (
        <div style={{ ...cardStyle, background: '#FFFBEB', borderColor: '#FDE68A', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: '#92400E' }}>
            No settings service is configured, so changes apply to <strong>this device only</strong>. Set
            <code style={{ margin: '0 4px', background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>VITE_SETTINGS_URL</code>
            (or <code style={{ background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>VITE_AGENT_URL</code>) to the settings service to apply changes for all users.
          </div>
        </div>
      )}

      {/* AI Assistant (chat) card */}
      <div style={cardStyle}>
        <div style={headRow}>
          <div style={iconChip('rgba(13,148,136,0.08)', '#0D9488')}><MessageSquare size={18} /></div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={h2Style}>AI Assistant (Chat)</h2>
            <p style={pStyle}>Show or hide the floating provider assistant across the portal (claims, eligibility, payments, self-service). When off, the chat button and panel do not render for any user.</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Switch checked={assistantEnabled} name='chatAssistantSwitch' data-cy='chatAssistantSwitch' disabled={assistantBusy} onChange={(e) => toggleAssistant(e.value)} />
          <span style={{ fontSize: 14, fontWeight: 600, color: assistantEnabled ? '#166534' : '#64748B' }}>{assistantEnabled ? 'Assistant is ON' : 'Assistant is OFF'}</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: '#94A3B8' }}>{global ? 'Applies to all users' : 'This device only'}</span>
        </div>
        {assistantBusy && <div style={{ ...okLine, color: '#64748B' }}>Saving…</div>}
        {!assistantBusy && assistantMsg && (
          <div style={assistantMsg.ok ? okLine : errLine}>{assistantMsg.ok ? <CheckCircle size={14} /> : <AlertTriangle size={14} />} {assistantMsg.text}</div>
        )}

        {/* Sub-toggle: pre-login (sign-in page) public assistant. */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #E2E8F0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Switch checked={loginAssistant} name='loginAssistantSwitch' data-cy='loginAssistantSwitch' disabled={loginBusy} onChange={(e) => toggleLoginAssistant(e.value)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: loginAssistant ? '#166534' : '#334155' }}>
                Sign-in page help assistant {loginAssistant ? '(ON)' : '(OFF)'}
              </div>
              <p style={{ ...pStyle, margin: '2px 0 0' }}>
                A limited public helper on the login screen for general questions (create account, reset password/username,
                EFT/ERA, contact). It answers general info only — it cannot access any member, claim, eligibility, or payment
                data, and it’s rate-limited and bot-gated. Off by default.
              </p>
            </div>
          </div>
          {loginBusy && <div style={{ ...okLine, color: '#64748B' }}>Saving…</div>}
          {!loginBusy && loginMsg && (
            <div style={loginMsg.ok ? okLine : errLine}>{loginMsg.ok ? <CheckCircle size={14} /> : <AlertTriangle size={14} />} {loginMsg.text}</div>
          )}
        </div>
      </div>

      {/* Maintenance banner card */}
      <div style={cardStyle}>
        <div style={headRow}>
          <div style={iconChip('rgba(217,119,6,0.10)', '#D97706')}><Megaphone size={18} /></div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={h2Style}>Maintenance Notice</h2>
            <p style={pStyle}>Show a banner on the login screen and top of the portal for scheduled maintenance or when the system is unavailable. All users see it.</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <Switch checked={maintEnabled} name='maintananceNoticeSwitch' data-cy='maintananceNoticeSwitch' disabled={maintBusy} onChange={(e) => setMaintEnabled(e.value)} />
          <span style={{ fontSize: 14, fontWeight: 600, color: maintEnabled ? '#B45309' : '#64748B' }}>{maintEnabled ? 'Banner is showing' : 'Banner is hidden'}</span>
        </div>
        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Message</label>
        <TextArea value={maintMessage} data-cy='maintenanceMessage' onChange={(e) => setMaintMessage(e.value)} placeholder="e.g. Scheduled maintenance Sat 10 PM–2 AM CT. The portal may be briefly unavailable." rows={2} style={{ width: '100%' }} />
        {maintEnabled && maintMessage.trim() && (
          <div style={{ marginTop: 14, background: 'linear-gradient(90deg, #B45309, #92400E)', padding: '10px 16px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontSize: 13, fontWeight: 600 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0 }} /><span>Notice: {maintMessage}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button themeColor="primary" rounded="medium" disabled={maintBusy} data-cy='saveMaintananceButton' onClick={() => saveMaintenance(maintEnabled, maintMessage)}>Save</Button>
                  <Button fillMode="outline" rounded="medium" disabled={maintBusy} data-cy='turnOffMaintananceButton' onClick={clearMaintenance}><RotateCcw size={14} style={{ marginRight: 4 }} /> Turn off banner</Button>
          <span style={{ fontSize: 12, color: '#94A3B8' }}>{global ? 'Applies to all users' : 'This device only'}</span>
        </div>
        {maintBusy && <div style={{ ...okLine, color: '#64748B' }}>Saving…</div>}
        {!maintBusy && maintMsg && (
          <div style={maintMsg.ok ? okLine : errLine}>{maintMsg.ok ? <CheckCircle size={14} /> : <AlertTriangle size={14} />} {maintMsg.text}</div>
        )}
      </div>

      {/* Inactivity auto-deactivation card (PHI safeguard) */}
      <div style={cardStyle}>
        <div style={headRow}>
          <div style={iconChip('rgba(37,99,235,0.10)', '#2563EB')}><Clock size={18} /></div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={h2Style}>Auto-deactivate inactive accounts</h2>
            <p style={pStyle}>Automatically set an account to <em>Inactive</em> after a period with no sign-in. A HIPAA-aligned safeguard (NIST AC-2(3)) that limits PHI exposure from dormant accounts. Enforced by a scheduled job; users reactivate via an admin.</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                  <Switch checked={inact.enabled} name='autoDeactActivateSwitch' data-cy='autoDeactActivateSwitch' disabled={inactBusy} onChange={(e) => setInact((s) => ({ ...s, enabled: e.value }))} />
          <span style={{ fontSize: 14, fontWeight: 600, color: inact.enabled ? '#166534' : '#64748B' }}>{inact.enabled ? 'Auto-deactivation is ON' : 'Auto-deactivation is OFF'}</span>
        </div>
        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Deactivate after (days of inactivity)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <input
            type="number" min={1} max={3650} value={inact.days} data-cy='inactivaDays' disabled={inactBusy || !inact.enabled}
            onChange={(e) => setInact((s) => ({ ...s, days: e.target.value }))}
            style={{ width: 110, background: 'var(--bg-input,#F8FAFB)', border: '1px solid var(--border-color,#E8ECF0)', borderRadius: 8, padding: '9px 12px', font: 'inherit', fontSize: 14, color: 'var(--text-primary,#1A1D21)', outline: 'none' }}
          />
          <span style={{ fontSize: 12.5, color: '#94A3B8' }}>Common values: 30, 60, or 90 days.</span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button themeColor="primary" data-cy='saveActiveDeactiveStatus' rounded="medium" disabled={inactBusy} onClick={() => saveInactivity(inact.enabled, inact.days)}>Save</Button>
          <span style={{ fontSize: 12, color: '#94A3B8' }}>{global ? 'Applies to all users' : 'Requires a settings service'}</span>
        </div>
        {inactBusy && <div style={{ ...okLine, color: '#64748B' }}>Saving…</div>}
        {!inactBusy && inactMsg && (
          <div style={inactMsg.ok ? okLine : errLine}>{inactMsg.ok ? <CheckCircle size={14} /> : <AlertTriangle size={14} />} {inactMsg.text}</div>
        )}
      </div>
    </div>
  );
}
